import { z } from 'zod'
import { sourceChannelSchema } from '../enums/source-channel.enum.js'

/**
 * Where a venue's couples came from, for one period and view
 * (docs/ad-attribution.spec.md): one row per source, with its money for those
 * who see ad costs. Money fields are null without Ads View, and wherever
 * nothing records the money.
 */

export const sourceRowDtoSchema = z.object({
  channel: sourceChannelSchema,
  /** For a listing site: which. */
  listingId: z.string().uuid().nullable(),
  /** For a Google Ads campaign: which; null on the row of Google Ads couples with no campaign known. */
  campaignId: z.string().uuid().nullable(),
  /** "Google Ads, no campaign", a campaign's or listing's name, or the channel's. */
  label: z.string(),
  inquiries: z.number().int().nonnegative(),
  /** Null where nothing at the venue records a tour. */
  tours: z.number().int().nonnegative().nullable(),
  bookings: z.number().int().nonnegative(),
  /** Bookings counted that carry no value: booked by hand at a venue that uses Sales. */
  bookingsWithoutValue: z.number().int().nonnegative(),
  /** US cents, tax included. Canceled bookings add none. */
  bookedValueCents: z.number().int().nonnegative().nullable(),
  /** Millionths of `currencyCode`: Google's spend, or a listing's cost in US dollars. */
  costMicros: z.number().int().nonnegative().nullable(),
  currencyCode: z.string().nullable(),
  costPerInquiryMicros: z.number().int().nonnegative().nullable(),
  costPerBookingMicros: z.number().int().nonnegative().nullable(),
  /** Booked value per dollar spent, e.g. 4.2; null with no cost, no value, or the cost in another currency. */
  returnRatio: z.number().nonnegative().nullable(),
})

export type SourceRowDto = z.infer<typeof sourceRowDtoSchema>

/**
 * Why a return is missing or partial: no booked values recorded (`no_values`),
 * costs in another currency (`currencies`), or, for the company, some venues
 * recording no values, whose costs its return leaves out (`some_values`).
 */
const SOURCE_SUMMARY_MISSING = ['no_values', 'currencies', 'some_values'] as const

/** A venue's or company's booked value against all it spent: for those who see ad costs. */
export const sourceSummaryDtoSchema = z.object({
  bookedValueCents: z.number().int().nonnegative().nullable(),
  /** Google spend and listing costs together; null when nothing records a cost, or they can't be added up. */
  costMicros: z.number().int().nonnegative().nullable(),
  currencyCode: z.string().nullable(),
  returnRatio: z.number().nonnegative().nullable(),
  /** Why the return is missing or partial: see `SOURCE_SUMMARY_MISSING`. */
  missing: z.enum(SOURCE_SUMMARY_MISSING).nullable(),
})

export type SourceSummaryDto = z.infer<typeof sourceSummaryDtoSchema>
